import { VavClient } from '@vav/client-core';
import { CallManager } from './call.ts';
import { createStore } from './idbStore.ts';
import { listProfiles } from './profiles.ts';

/**
 * Keeps every profile you are *not* looking at connected.
 *
 * The point of several profiles is that they behave like several mailboxes: a
 * message to the work account has to ring while the personal one is on screen,
 * or the feature is just a slower way of logging out and back in.
 *
 * So each inactive profile gets its own client, its own socket and its own
 * store, running alongside the one React is rendering. They are read-only from
 * the UI's point of view — nothing here sends anything. All they do is stay
 * connected, drain their queue, and report an unread count.
 *
 * Push needs no coordination between them, which is worth stating because it
 * looks like it should. A browser has one push subscription per origin, but the
 * server's `push_tokens` table is keyed `(user_id, device_id)` with a
 * non-unique index on the token, so every profile registers the *same*
 * subscription against its own account and the server wakes the endpoint for
 * whichever account received something. The wake carries no account id — it
 * does not need to, because the page drains all of them.
 */
export interface ProfileSummary {
  profileId: string;
  label: string;
  username?: string;
  displayName?: string;
  unread: number;
}

type Listener = (summaries: ProfileSummary[]) => void;

export class BackgroundProfiles {
  #serverUrl: string;
  #clients = new Map<string, VavClient>();
  /**
   * One call manager per background profile, so a call to any of your
   * accounts rings this device no matter which one is on screen. Created with
   * the client, not with any UI: the listener has to exist before the ring
   * arrives.
   */
  #callManagers = new Map<string, CallManager>();
  #summaries = new Map<string, ProfileSummary>();
  #listeners = new Set<Listener>();
  #activeId?: string;

  constructor(serverUrl: string) {
    this.#serverUrl = serverUrl;
  }

  subscribe(listener: Listener): () => void {
    this.#listeners.add(listener);
    listener(this.snapshot());
    return () => this.#listeners.delete(listener);
  }

  snapshot(): ProfileSummary[] {
    return listProfiles().map(
      (profile) =>
        this.#summaries.get(profile.id) ?? {
          profileId: profile.id,
          label: profile.label,
          unread: 0,
        },
    );
  }

  /**
   * Connect everything except `activeId`, which the React tree owns.
   *
   * Safe to call repeatedly: it reconciles against what is already running, so
   * a profile added or switched to does not restart the others.
   */
  async sync(activeId: string): Promise<void> {
    this.#activeId = activeId;
    const wanted = new Set(listProfiles().map((p) => p.id).filter((id) => id !== activeId));

    for (const [id, client] of this.#clients) {
      if (wanted.has(id)) continue;
      // Now active, or deleted. Either way this instance must let go of the
      // database before the React tree opens it.
      this.#callManagers.get(id)?.dispose();
      this.#callManagers.delete(id);
      client.disconnect();
      this.#clients.delete(id);
      this.#summaries.delete(id);
    }

    await Promise.all([...wanted].filter((id) => !this.#clients.has(id)).map((id) => this.#start(id)));
    this.#emit();
  }

  /**
   * The connected background clients, for callers that need to reach them —
   * sharing the push subscription, principally.
   */
  clients(): VavClient[] {
    return [...this.#clients.values()];
  }

  /** Profiles that hold a registered account right now (background only). */
  registeredIds(): string[] {
    return [...this.#clients.keys()];
  }

  /** Every background profile's call surface, for the app root to render. */
  callManagers(): { profileId: string; label: string; client: VavClient; manager: CallManager }[] {
    return [...this.#callManagers.entries()].map(([profileId, manager]) => ({
      profileId,
      label: listProfiles().find((p) => p.id === profileId)?.label ?? '',
      client: this.#clients.get(profileId)!,
      manager,
    }));
  }

  /** Ask every background profile to drain, after a push woke the page. */
  async drainAll(): Promise<void> {
    await Promise.all(
      [...this.#clients.values()].map(async (client) => {
        try {
          await client.connect();
        } catch {
          // Offline. The socket retries on its own.
        }
      }),
    );
  }

  stop(): void {
    for (const manager of this.#callManagers.values()) manager.dispose();
    this.#callManagers.clear();
    for (const client of this.#clients.values()) client.disconnect();
    this.#clients.clear();
    this.#summaries.clear();
  }

  async #start(profileId: string): Promise<void> {
    const label = listProfiles().find((p) => p.id === profileId)?.label ?? '';
    try {
      const client = new VavClient({
        serverUrl: this.#serverUrl,
        store: await createStore(profileId),
      });

      // An unregistered profile has nothing to connect with. It still belongs
      // in the list -- it is how the user finishes signing it up.
      const registered = await client.load();
      if (!registered) {
        this.#summaries.set(profileId, { profileId, label, unread: 0 });
        this.#emit();
        return;
      }

      this.#clients.set(profileId, client);
      const manager = new CallManager(client);
      this.#callManagers.set(profileId, manager);
      // A ring changes no message and no conversation, so the usual refresh
      // never fires; the overlay still has to appear.
      manager.subscribe(() => this.#emit());
      const refresh = () => {
        this.#summaries.set(profileId, {
          profileId,
          label,
          username: client.account?.username,
          displayName: client.account?.displayName,
          unread: client.conversations.reduce((sum, c) => sum + (c.unread ?? 0), 0),
        });
        this.#emit();
      };

      client.on('message', refresh);
      client.on('conversation', refresh);
      refresh();

      await client.connect();
    } catch {
      // A profile that cannot boot must not take the app down with it: the one
      // the user is actually looking at is a different client entirely.
      this.#summaries.set(profileId, { profileId, label, unread: 0 });
      this.#emit();
    }
  }

  #emit(): void {
    const snapshot = this.snapshot();
    for (const listener of this.#listeners) listener(snapshot);
  }

  get activeId(): string | undefined {
    return this.#activeId;
  }
}
